// client/src/coms/views/canvas_parts/canvas_room_view_parts/CanvasLayersPanel.js
import React, { useMemo, useState } from 'react';

function clampOpacityValue(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 1;
  return Math.max(0, Math.min(1, numericValue));
}

function getLayerDisplayName(layer, index) {
  const name = String(layer && layer.name ? layer.name : '').trim();
  if (name) return name;
  return 'Layer ' + (index + 1);
}

function getLayerOwnerName(layer, fallback = '') {
  const ownerName = String(layer && layer.ownerName ? layer.ownerName : '').trim();
  if (ownerName) return ownerName;
  return String(fallback || '').trim();
}

function getLayerOwnerUserId(layer) {
  const ownerUserId = Number(layer && layer.ownerUserId);
  if (Number.isInteger(ownerUserId) && ownerUserId > 0) return ownerUserId;

  const layerId = String(layer && layer.id ? layer.id : '').trim();
  const match = layerId.match(/^remote-layer-user-(\d+)$/) || layerId.match(/^remote-layer-user-(\d+)-/);
  if (match && Number.isInteger(Number(match[1]))) return Number(match[1]);

  const ownerKey = String(layer && layer.ownerKey ? layer.ownerKey : '').trim();
  const ownerKeyMatch = ownerKey.match(/^user-(\d+)$/);
  if (ownerKeyMatch && Number.isInteger(Number(ownerKeyMatch[1]))) return Number(ownerKeyMatch[1]);

  return 0;
}

function normaliseName(value) {
  return String(value || '').trim().toLowerCase();
}

function isLayerForCurrentUser(layer, currentUserId, currentUserName, separateUserLayers) {
  if (!layer) return false;
  if (separateUserLayers === false) return true;
  if (layer.sharedLayer === true || layer.editableByOthers === true) return true;

  const safeCurrentUserId = Number(currentUserId);
  const layerOwnerUserId = getLayerOwnerUserId(layer);
  if (Number.isInteger(safeCurrentUserId) && safeCurrentUserId > 0 && layerOwnerUserId > 0) {
    return layerOwnerUserId === safeCurrentUserId;
  }

  const currentName = normaliseName(currentUserName);
  const ownerName = normaliseName(layer.ownerName);
  if (currentName && ownerName) return currentName === ownerName;

  if (layer.isRemoteUserLayer) return false;
  return true;
}

function canRunLayerAction(callback, layer, fallback = true) {
  if (typeof callback !== 'function') return fallback;
  return callback(layer) !== false;
}

function LayerIconButton({ label, title, disabled, onClick, children, danger = false }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={title || label}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      style={{
        width: 22,
        height: 22,
        borderRadius: 8,
        border: '1px solid rgba(255,255,255,0.11)',
        background: disabled
          ? 'rgba(255,255,255,0.025)'
          : danger
            ? 'rgba(239,68,68,0.16)'
            : 'rgba(255,255,255,0.07)',
        color: disabled
          ? 'rgba(255,255,255,0.26)'
          : danger
            ? 'rgba(254,202,202,0.96)'
            : 'rgba(255,255,255,0.82)',
        cursor: disabled ? 'default' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 900,
        lineHeight: 1,
        padding: 0,
        flex: '0 0 auto',
      }}
    >
      {children}
    </button>
  );
}

export default function CanvasLayersPanel({
  layers = [],
  activeLayerId = '',
  onAddLayer,
  onSelectLayer,
  onMoveLayerUp,
  onMoveLayerDown,
  onToggleLayerVisibility,
  onToggleLayerHiddenFromOthers,
  onToggleMyLayersHiddenFromOthers,
  onDeleteLayer,
  onRenameLayer,
  onLayerOpacityChange,
  onLayerEdgeWearChange,
  onLayerNormalSettingsChange,
  modelViewOpen = false,
  separateUserLayers = true,
  onSeparateUserLayersChange,
  separateUserLayersLocked = false,
  canEditLayer,
  canDeleteLayer,
  canRenameLayer,
  currentUserId = 0,
  currentUserName = '',
}) {
  const safeLayers = Array.isArray(layers) ? layers.filter(Boolean) : [];
  const [editingLayerId, setEditingLayerId] = useState('');
  const [editingName, setEditingName] = useState('');

  const layerRows = useMemo(() => safeLayers.map((layer, index) => {
    const ownerName = getLayerOwnerName(layer, isLayerForCurrentUser(layer, currentUserId, currentUserName, separateUserLayers) ? 'Me' : 'Remote user');
    const isMine = isLayerForCurrentUser(layer, currentUserId, currentUserName, separateUserLayers);
    return {
      layer,
      index,
      isMine,
      ownerName,
      displayName: getLayerDisplayName(layer, index),
      opacity: clampOpacityValue(layer.opacity),
      visible: layer.visible !== false,
      hiddenFromOthers: layer.hiddenFromOthers === true,
      canHideFromOthers: isMine,
      active: String(layer.id || '') === String(activeLayerId || ''),
      editable: canRunLayerAction(canEditLayer, layer, true),
      deletable: canRunLayerAction(canDeleteLayer, layer, safeLayers.length > 1),
      renameable: canRunLayerAction(canRenameLayer, layer, true),
    };
  }), [activeLayerId, canDeleteLayer, canEditLayer, canRenameLayer, currentUserId, currentUserName, safeLayers, separateUserLayers]);

  const myRows = layerRows.filter((row) => row.isMine);
  const hideableMineRows = myRows.filter((row) => row.canHideFromOthers);
  const allMineHiddenFromOthers = hideableMineRows.length > 0 && hideableMineRows.every((row) => row.hiddenFromOthers);

  const commitRename = (layer) => {
    const nextName = String(editingName || '').trim();
    setEditingLayerId('');
    setEditingName('');
    if (!nextName) return;
    if (typeof onRenameLayer === 'function') {
      onRenameLayer(layer.id, nextName);
    }
  };

  const startRename = (row) => {
    if (!row.renameable) return;
    setEditingLayerId(String(row.layer.id || ''));
    setEditingName(row.displayName);
  };

  return (
    <section
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minWidth: 0,
        color: 'rgba(255,255,255,0.92)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          minWidth: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 950, letterSpacing: 0.2, lineHeight: 1.1 }}>Layers</div>
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.48)', fontWeight: 800, marginTop: 2 }}>
            {safeLayers.length} total
          </div>
        </div>
        <button
          type="button"
          onClick={typeof onAddLayer === 'function' ? onAddLayer : undefined}
          disabled={typeof onAddLayer !== 'function'}
          style={{
            height: 26,
            borderRadius: 10,
            border: '1px solid rgba(96,165,250,0.34)',
            background: 'rgba(59,130,246,0.18)',
            color: 'rgba(219,234,254,0.96)',
            padding: '0 10px',
            fontSize: 11,
            fontWeight: 950,
            cursor: typeof onAddLayer === 'function' ? 'pointer' : 'default',
          }}
        >
          + Layer
        </button>
      </div>

      {typeof onSeparateUserLayersChange === 'function' ? (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.035)',
            padding: '7px 8px',
            fontSize: 10,
            fontWeight: 850,
            color: separateUserLayersLocked ? 'rgba(255,255,255,0.38)' : 'rgba(255,255,255,0.72)',
          }}
        >
          <span>Separate user layers</span>
          <input
            type="checkbox"
            checked={separateUserLayers !== false}
            disabled={separateUserLayersLocked}
            onChange={(event) => onSeparateUserLayersChange(event.target.checked)}
            style={{ margin: 0, accentColor: '#60a5fa' }}
          />
        </label>
      ) : null}

      {hideableMineRows.length ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(0,0,0,0.16)',
            padding: '7px 8px',
          }}
        >
          {hideableMineRows.length ? (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                fontSize: 10,
                fontWeight: 850,
                color: 'rgba(255,255,255,0.72)',
              }}
            >
              <span>Hide all my layers from others</span>
              <input
                type="checkbox"
                checked={allMineHiddenFromOthers}
                disabled={typeof onToggleMyLayersHiddenFromOthers !== 'function'}
                onChange={() => onToggleMyLayersHiddenFromOthers()}
                style={{ margin: 0, accentColor: '#f59e0b' }}
              />
            </label>
          ) : null}
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          minWidth: 0,
        }}
      >
        {layerRows.length ? layerRows.map((row) => {
          const layerId = String(row.layer.id || '');
          const isEditing = editingLayerId === layerId;
          const justifyContent = row.isMine ? 'flex-end' : 'flex-start';
          const bubbleRadius = row.isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px';
          const bubbleBackground = row.active
            ? row.isMine
              ? 'linear-gradient(135deg, rgba(59,130,246,0.46), rgba(37,99,235,0.26))'
              : 'linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.07))'
            : row.isMine
              ? 'rgba(37,99,235,0.20)'
              : 'rgba(255,255,255,0.055)';
          const bubbleBorder = row.active
            ? row.isMine
              ? '1px solid rgba(147,197,253,0.60)'
              : '1px solid rgba(255,255,255,0.22)'
            : row.isMine
              ? '1px solid rgba(96,165,250,0.28)'
              : '1px solid rgba(255,255,255,0.09)';

          return (
            <div
              key={layerId || row.index}
              style={{
                display: 'flex',
                justifyContent,
                minWidth: 0,
              }}
            >
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (typeof onSelectLayer === 'function') onSelectLayer(row.layer.id);
                }}
                onKeyDown={(event) => {
                  if ((event.key === 'Enter' || event.key === ' ') && typeof onSelectLayer === 'function') {
                    event.preventDefault();
                    onSelectLayer(row.layer.id);
                  }
                }}
                style={{
                  width: 'min(88%, 232px)',
                  borderRadius: bubbleRadius,
                  border: bubbleBorder,
                  background: bubbleBackground,
                  boxShadow: row.active ? '0 8px 24px rgba(0,0,0,0.24)' : 'none',
                  padding: '7px 8px',
                  boxSizing: 'border-box',
                  cursor: row.isMine ? 'pointer' : 'default',
                  opacity: row.visible ? 1 : 0.45,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    minWidth: 0,
                    flexDirection: row.isMine ? 'row-reverse' : 'row',
                  }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 8,
                      background: row.isMine ? 'rgba(147,197,253,0.25)' : 'rgba(255,255,255,0.09)',
                      border: row.isMine ? '1px solid rgba(191,219,254,0.28)' : '1px solid rgba(255,255,255,0.10)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 9,
                      fontWeight: 950,
                      color: 'rgba(255,255,255,0.82)',
                      flex: '0 0 auto',
                    }}
                  >
                    {row.isMine ? 'Me' : 'U'}
                  </div>

                  {row.canHideFromOthers ? (
                    <label
                      title={row.hiddenFromOthers ? 'Shown only to you' : 'Visible to other users'}
                      onClick={(event) => event.stopPropagation()}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                        height: 18,
                        borderRadius: 7,
                        border: '1px solid rgba(255,255,255,0.11)',
                        background: row.hiddenFromOthers ? 'rgba(245,158,11,0.18)' : 'rgba(255,255,255,0.045)',
                        color: row.hiddenFromOthers ? 'rgba(254,243,199,0.96)' : 'rgba(255,255,255,0.58)',
                        padding: '0 5px',
                        fontSize: 8,
                        fontWeight: 900,
                        cursor: typeof onToggleLayerHiddenFromOthers === 'function' ? 'pointer' : 'default',
                        flex: '0 0 auto',
                      }}
                    >
                      <span>private</span>
                      <input
                        type="checkbox"
                        checked={row.hiddenFromOthers}
                        disabled={typeof onToggleLayerHiddenFromOthers !== 'function'}
                        onChange={() => onToggleLayerHiddenFromOthers(row.layer.id)}
                        style={{ margin: 0, accentColor: '#f59e0b' }}
                      />
                    </label>
                  ) : null}

                  <div style={{ minWidth: 0, flex: '1 1 auto', textAlign: row.isMine ? 'right' : 'left' }}>
                    {isEditing ? (
                      <input
                        autoFocus
                        value={editingName}
                        onChange={(event) => setEditingName(event.target.value)}
                        onBlur={() => commitRename(row.layer)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') commitRename(row.layer);
                          if (event.key === 'Escape') {
                            setEditingLayerId('');
                            setEditingName('');
                          }
                        }}
                        onClick={(event) => event.stopPropagation()}
                        style={{
                          width: '100%',
                          height: 22,
                          borderRadius: 8,
                          border: '1px solid rgba(255,255,255,0.16)',
                          background: 'rgba(0,0,0,0.22)',
                          color: 'rgba(255,255,255,0.95)',
                          fontSize: 11,
                          fontWeight: 850,
                          outline: 'none',
                          padding: '0 7px',
                          boxSizing: 'border-box',
                          textAlign: row.isMine ? 'right' : 'left',
                        }}
                      />
                    ) : (
                      <div
                        onDoubleClick={(event) => {
                          event.stopPropagation();
                          startRename(row);
                        }}
                        title={row.displayName}
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: 11,
                          fontWeight: row.active ? 950 : 850,
                          color: 'rgba(255,255,255,0.94)',
                          lineHeight: 1.15,
                        }}
                      >
                        {row.displayName}
                      </div>
                    )}
                    <div
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 8.5,
                        fontWeight: 800,
                        color: 'rgba(255,255,255,0.48)',
                        marginTop: 2,
                      }}
                    >
                      {row.isMine ? 'You' : row.ownerName || 'Remote user'} · {Math.round(row.opacity * 100)}%{row.isMine ? '' : ' · locked'}{row.hiddenFromOthers ? ' · private' : ''}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 6,
                    alignItems: 'center',
                    marginTop: 6,
                  }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <input
                    aria-label={row.displayName + ' opacity'}
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={Math.round(row.opacity * 100)}
                    disabled={!row.editable || typeof onLayerOpacityChange !== 'function'}
                    onChange={(event) => {
                      if (typeof onLayerOpacityChange === 'function') {
                        onLayerOpacityChange(row.layer.id, Number(event.target.value) / 100);
                      }
                    }}
                    style={{ width: '100%', accentColor: row.isMine ? '#93c5fd' : '#d1d5db' }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: row.isMine ? 'flex-end' : 'flex-start',
                      gap: 4,
                      flexDirection: row.isMine ? 'row-reverse' : 'row',
                    }}
                  >
                    <LayerIconButton
                      label={row.visible ? 'Hide layer' : 'Show layer'}
                      onClick={() => {
                        if (typeof onToggleLayerVisibility === 'function') onToggleLayerVisibility(row.layer.id);
                      }}
                      disabled={!row.editable || typeof onToggleLayerVisibility !== 'function'}
                    >
                      {row.visible ? '👁' : '○'}
                    </LayerIconButton>
                    <LayerIconButton
                      label="Move layer up"
                      onClick={() => {
                        if (typeof onMoveLayerUp === 'function') onMoveLayerUp(row.layer.id);
                      }}
                      disabled={!row.editable || typeof onMoveLayerUp !== 'function' || row.index >= safeLayers.length - 1}
                    >
                      ↑
                    </LayerIconButton>
                    <LayerIconButton
                      label="Move layer down"
                      onClick={() => {
                        if (typeof onMoveLayerDown === 'function') onMoveLayerDown(row.layer.id);
                      }}
                      disabled={!row.editable || typeof onMoveLayerDown !== 'function' || row.index <= 0}
                    >
                      ↓
                    </LayerIconButton>
                    <LayerIconButton
                      label="Rename layer"
                      onClick={() => startRename(row)}
                      disabled={!row.renameable || typeof onRenameLayer !== 'function'}
                    >
                      ✎
                    </LayerIconButton>
                    <LayerIconButton
                      label="Delete layer"
                      danger
                      onClick={() => {
                        if (typeof onDeleteLayer === 'function') onDeleteLayer(row.layer.id);
                      }}
                      disabled={!row.deletable || typeof onDeleteLayer !== 'function'}
                    >
                      ×
                    </LayerIconButton>
                  </div>
                </div>

                {modelViewOpen && row.active ? (() => {
                  const edgeWearPercent = Math.max(-100, Math.min(100, Number(row.layer.edgeWearPercent ?? 0) || 0));
                  const normalSourceLayerId = String(row.layer.normalSourceLayerId || '');
                  const normalSelfPercent = Math.max(0, Math.min(100, Number(row.layer.normalSelfPercent ?? 100) || 0));
                  const normalSourceOptions = layerRows.filter((candidate) => String(candidate.layer.id || '') !== layerId);
                  return (
                    <div
                      onClick={(event) => event.stopPropagation()}
                      style={{
                        marginTop: 8,
                        paddingTop: 8,
                        borderTop: '1px solid rgba(255,255,255,0.10)',
                      }}
                    >
                      <div style={{ fontSize: 10, fontWeight: 950, color: 'rgba(255,255,255,0.88)' }}>Paint Distribution</div>
                      <div style={{ marginTop: 5, fontSize: 9, fontWeight: 850, color: 'rgba(255,255,255,0.66)' }}>
                        {edgeWearPercent === 0
                          ? '0 — unchanged'
                          : edgeWearPercent < 0
                            ? `${Math.abs(edgeWearPercent)}% toward edges`
                            : `${edgeWearPercent}% away from edges`}
                      </div>
                      <input
                        aria-label={row.displayName + ' paint distribution'}
                        type="range"
                        min="-100"
                        max="100"
                        step="1"
                        value={edgeWearPercent}
                        disabled={!row.editable || typeof onLayerEdgeWearChange !== 'function'}
                        onChange={(event) => onLayerEdgeWearChange(row.layer.id, Math.max(-100, Math.min(100, Number(event.target.value) || 0)))}
                        style={{
                          width: '100%',
                          marginTop: 5,
                          accentColor: '#fbbf24',
                        }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 1, marginBottom: 10, fontSize: 8, color: 'rgba(255,255,255,0.42)' }}>
                        <span>Keep edges</span>
                        <span>0</span>
                        <span>Keep centre</span>
                      </div>

                      <div style={{ fontSize: 10, fontWeight: 950, color: 'rgba(255,255,255,0.88)' }}>Normal source</div>
                      <select
                        aria-label={row.displayName + ' normal source'}
                        value={normalSourceLayerId}
                        disabled={!row.editable || typeof onLayerNormalSettingsChange !== 'function'}
                        onChange={(event) => onLayerNormalSettingsChange(row.layer.id, { normalSourceLayerId: event.target.value })}
                        style={{
                          width: '100%',
                          marginTop: 6,
                          height: 28,
                          borderRadius: 8,
                          border: '1px solid rgba(255,255,255,0.14)',
                          background: 'rgba(0,0,0,0.26)',
                          color: 'rgba(255,255,255,0.94)',
                          padding: '0 7px',
                          fontSize: 10,
                          fontWeight: 800,
                        }}
                      >
                        <option value="">No other layer</option>
                        <option value="__all_below__">All visible materials below</option>
                        {normalSourceOptions.map((candidate) => (
                          <option key={String(candidate.layer.id || '')} value={String(candidate.layer.id || '')}>
                            {candidate.displayName}
                          </option>
                        ))}
                      </select>

                      <div style={{ marginTop: 7, fontSize: 9, fontWeight: 850, color: 'rgba(255,255,255,0.66)' }}>
                        {100 - normalSelfPercent}% {normalSourceLayerId === '__all_below__' ? 'below' : 'chosen'} / {normalSelfPercent}% this layer
                      </div>
                      <input
                        aria-label={row.displayName + ' normal blend'}
                        type="range"
                        min="-100"
                        max="100"
                        step="1"
                        value={normalSelfPercent}
                        disabled={!row.editable || !normalSourceLayerId || typeof onLayerNormalSettingsChange !== 'function'}
                        onChange={(event) => onLayerNormalSettingsChange(row.layer.id, {
                          normalSelfPercent: Math.max(0, Math.min(100, Number(event.target.value) || 0)),
                        })}
                        style={{
                          width: '100%',
                          marginTop: 5,
                          accentColor: '#93c5fd',
                          opacity: normalSourceLayerId ? 1 : 0.45,
                        }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 1, fontSize: 8, color: 'rgba(255,255,255,0.42)' }}>
                        <span>{normalSourceLayerId === '__all_below__' ? 'Materials below' : 'Chosen layer'}</span>
                        <span>This layer</span>
                      </div>
                    </div>
                  );
                })() : null}
              </div>
            </div>
          );
        }) : (
          <div
            style={{
              borderRadius: 14,
              border: '1px dashed rgba(255,255,255,0.14)',
              background: 'rgba(255,255,255,0.035)',
              padding: 14,
              textAlign: 'center',
              fontSize: 11,
              fontWeight: 800,
              color: 'rgba(255,255,255,0.48)',
            }}
          >
            No layers yet
          </div>
        )}
      </div>
    </section>
  );
}
